import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiCheck, FiChevronLeft, FiChevronRight, FiPlus, FiMinus } from "react-icons/fi";
import { useInView } from "../components/useInView";
import heroBg from "../assets/uploads/about/1771700769580-90475154.jpg";
import "./About.css";

/* ── hero background ── */

/* ── intro image (replaced with company logo) ── */
import introImg from "../assets/uploads/logos/image_02.jpg";

/* ── intro corner icon ── */
import iconLogo3 from "../assets/uploads/about/icon-logo3.png";

/* ── tab panel images ── */
import tabImg1 from "../assets/uploads/about/1771700760912-141930105.jpg";
import tabImg2 from "../assets/uploads/about/1771700769580-90475154.jpg";
import tabImg3 from "../assets/uploads/about/1771700776853-975274538.jpg";

/* ── FAQ images (static imports — Vite requires compile-time paths) ── */
import faqImg1 from "../assets/uploads/faq/1771700760912-141930105.jpg";
import faqImg2 from "../assets/uploads/faq/1771700769580-90475154.jpg";
import faqImg3 from "../assets/uploads/faq/1771700776853-975274538.jpg";
import faqImg4 from "../assets/uploads/faq/1771700784083-802325146.jpg";
import faqImg5 from "../assets/uploads/faq/1771700795154-611491162.jpg";
import faqImg6 from "../assets/uploads/faq/1771764363343-641904961.jpg";

/* ── service SVG icons (reused for values) ── */
import iconIntegrity  from "../assets/uploads/services/integrity.svg";
import iconSafety     from "../assets/uploads/services/safety.svg";
import iconExcellence from "../assets/uploads/services/excellence.svg";
import iconCustomer   from "../assets/uploads/services/customer.svg";
import iconReliable   from "../assets/uploads/services/reliable.svg";
import iconPassion    from "../assets/uploads/services/passion.svg";

/* ── car brand SVG logos ── */
import logoFord       from "../assets/uploads/cars/ford.svg";
import logoVolkswagen from "../assets/uploads/cars/volkswagen.svg";
import logoAudi       from "../assets/uploads/cars/audi.svg";
import logoSuzuki     from "../assets/uploads/cars/suzuki.svg";
import logoHyundai    from "../assets/uploads/cars/hyundai.svg";
import logoHonda      from "../assets/uploads/cars/honda.svg";
import logoBMW        from "../assets/uploads/cars/bmw.svg";
import logoKia        from "../assets/uploads/cars/kia.svg";
import logoMercedes   from "../assets/uploads/cars/mercedes.svg";
import logoToyota     from "../assets/uploads/cars/toyota.svg";
import logoNissan     from "../assets/uploads/cars/nissan.svg";
import logoMazda      from "../assets/uploads/cars/mazda.svg";

/**
 * Map DB image_filename → imported asset.
 * Add more entries if you upload additional FAQ images.
 */
const FAQ_IMAGE_MAP = {
  "1771700760912-141930105.jpg": faqImg1,
  "1771700769580-90475154.jpg":  faqImg2,
  "1771700776853-975274538.jpg": faqImg3,
  "1771700784083-802325146.jpg": faqImg4,
  "1771700795154-611491162.jpg": faqImg5,
  "1771764363343-641904961.jpg": faqImg6,
};

const CAR_BRAND_LOGOS = [
  { name: "Ford",       src: logoFord       },
  { name: "Volkswagen", src: logoVolkswagen },
  { name: "Audi",       src: logoAudi       },
  { name: "Suzuki",     src: logoSuzuki     },
  { name: "Hyundai",    src: logoHyundai    },
  { name: "Honda",      src: logoHonda      },
  { name: "BMW",        src: logoBMW        },
  { name: "Kia",        src: logoKia        },
  { name: "Mercedes",   src: logoMercedes   },
  { name: "Toyota",     src: logoToyota     },
  { name: "Nissan",     src: logoNissan     },
  { name: "Mazda",      src: logoMazda      },
];

const VALUE_ICONS = [iconIntegrity, iconSafety, iconExcellence, iconCustomer, iconReliable, iconPassion];
const YOUTUBE_VIDEO_ID = "PVRqLIgtZ5o";

function useNoContextMenu() {
  useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);
}

function parseCoreValues(raw = "") {
  if (!raw) return [];
  return raw
    .split(/\n+/)
    .map(line => line.trim())
    .filter(line => line.includes(":"))
    .map(line => {
      const colonIdx = line.indexOf(":");
      return {
        title: line.slice(0, colonIdx).trim(),
        desc:  line.slice(colonIdx + 1).trim(),
      };
    })
    .filter(v => v.title && v.desc);
}

function parseObjectives(raw = "") {
  if (!raw) return [];
  return raw
    .split(".")
    .map(s => s.trim())
    .filter(s => s.length > 3);
}

/* ════════════════════════════════════════════════════════════ */
export default function About() {
  useNoContextMenu();

  /* ── API data ── */
  const [aboutData,    setAboutData]    = useState(null);
  const [loadingAbout, setLoadingAbout] = useState(true);
  const [faqData,      setFaqData]      = useState([]);
  const [loadingFaq,   setLoadingFaq]   = useState(true);
  const [openFaq,      setOpenFaq]      = useState(0);
  const [activeFaqImg, setActiveFaqImg] = useState(faqImg1);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/about-us`)
      .then(r => r.json())
      .then(data => setAboutData(data?.id ? data : null))
      .catch(err => console.error("About-us fetch failed:", err))
      .finally(() => setLoadingAbout(false));
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/faq`)
      .then(r => r.json())
      .then(data => {
        const items = Array.isArray(data) ? data : [];
        setFaqData(items);
        if (items.length > 0 && items[0].image_filename) {
          const mapped = FAQ_IMAGE_MAP[items[0].image_filename];
          if (mapped) setActiveFaqImg(mapped);
        }
      })
      .catch(err => console.error("FAQ fetch failed:", err))
      .finally(() => setLoadingFaq(false));
  }, []);

  /* ── Derived data ── */
  const metaDescription  = aboutData?.meta_description  || "";
  const mainContent      = aboutData?.content           || "";
  const missionStatement = aboutData?.mission_statement || "";
  const objectives       = parseObjectives(aboutData?.objectives || "");
  const coreValues       = parseCoreValues(aboutData?.core_values || "");

  const vidTabs = [
    {
      label: "About Noel Automotive Repair",
      img:   tabImg1,
      title: "About Noel Automotive Repair",
      bullets: mainContent
        ? mainContent.split(/\n+/).filter(s => s.trim().length > 5)
        : ["Loading content…"],
    },
    {
      label: "Mission Statement",
      img:   tabImg2,
      title: "Mission Statement",
      bullets: missionStatement
        ? missionStatement.split(/\n+/).filter(s => s.trim().length > 5)
        : ["Loading mission statement…"],
    },
    {
      label: "Our Objectives",
      img:   tabImg3,
      title: "Our Objectives",
      bullets: objectives.length > 0 ? objectives : ["Loading objectives…"],
    },
  ];

  /* ── Intersection observers ── */
  const [heroRef,   heroInView]   = useInView();
  const [introRef,  introInView]  = useInView();
  const [vidRef,    vidInView]    = useInView();
  const [valRef,    valInView]    = useInView();
  const [brandsRef, brandsInView] = useInView();
  const [faqRef,    faqInView]    = useInView();

  const [activeTab, setActiveTab] = useState(0);
  const tab = vidTabs[activeTab];

  /* ── FAQ toggle ── */
  const handleFaqToggle = (index, imageFilename) => {
    if (openFaq === index) return;
    setOpenFaq(index);
    if (imageFilename && FAQ_IMAGE_MAP[imageFilename]) {
      setActiveFaqImg(FAQ_IMAGE_MAP[imageFilename]);
    }
  };

  return (
    <main className="ab">

      {/* ══════ 1. HERO ══════ */}
      <section className="ab-hero" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="ab-hero__overlay" />
        <div className="container ab-hero__body" ref={heroRef}>
          <div className={`ab-hero__label fade-in ${heroInView ? "visible" : ""}`}>ABOUT US</div>
          <h1
            className={`ab-hero__title fade-in ${heroInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.1s" }}
          >
            TRUSTED AUTOMOTIVE<br />REPAIR SPECIALISTS
          </h1>
        </div>
        <div className="ab-hero__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={13} />
          <span>About Us</span>
        </div>
      </section>

      {/* ══════ 2. INTRO ══════ */}
      <section className="ab-intro" ref={introRef}>
        <div className="container ab-intro__inner">
          <div className={`ab-intro__img-col fade-in ${introInView ? "visible" : ""}`}>
            <img
              className="ab-intro__img-main"
              src={introImg}
              alt="Noel Automotive Repair"
              loading="lazy"
              draggable="false"
              onDragStart={e => e.preventDefault()}
            />
            <div className="ab-intro__img-accent" aria-hidden="true" />
            <div className="ab-intro__icon-badge">
              <img src={iconLogo3} alt="Noel Automotive logo" draggable="false" onDragStart={e => e.preventDefault()} />
            </div>
          </div>
          <div
            className={`ab-intro__right fade-in ${introInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.15s" }}
          >
            <div className="ab-intro__tag">WHO WE ARE</div>
            <h2 className="ab-intro__h2">
              Your Trusted Partner in<br /><span>Automotive Repair</span>
            </h2>
            {loadingAbout ? (
              <p className="ab-intro__block-text">Loading…</p>
            ) : (
              <div className="ab-intro__quote">
                <div className="ab-intro__bar" />
                <p>{metaDescription}</p>
              </div>
            )}
            <div className="ab-intro__block">
              <div className="ab-intro__block-label">FUEL &amp; YOUR DRIVING STYLE</div>
              <p className="ab-intro__block-text">
                We help you maintain your vehicle in a good condition and your driving style are the main factors influencing your fuel consumption.
              </p>
            </div>
            <Link to="/services" className="ab-intro__cta">Our Services <FiArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* ══════ 3. CAR BRAND LOGOS MARQUEE ══════ */}
      <section className="ab-partners" ref={brandsRef}>
        <div className="ab-partners__label-bar">
          <div className="container">
            <span className="ab-partners__heading">THE VEHICLES WE SPECIALISE ON</span>
          </div>
        </div>
        <div className="ab-partners__marquee">
          <div className="ab-partners__track">
            {[...CAR_BRAND_LOGOS, ...CAR_BRAND_LOGOS].map((brand, i) => (
              <div key={i} className="ab-partners__logo">
                <img
                  src={brand.src}
                  alt={brand.name}
                  title={brand.name}
                  loading="lazy"
                  draggable="false"
                  onDragStart={e => e.preventDefault()}
                  onContextMenu={e => e.preventDefault()}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ 4. VIDEO ══════ */}
      <section className="ab-video" ref={vidRef}>
        <div className="ab-video__bg ab-video__bg--yt">
          <iframe
            src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${YOUTUBE_VIDEO_ID}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
            title="Background video"
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
        </div>
        <div className="ab-video__overlay" aria-hidden="true" />
        <div className={`container ab-video__headline fade-in ${vidInView ? "visible" : ""}`}>
          <div className="ab-video__left">
            <div className="ab-video__tag">SUPPORTING THE FUTURE</div>
            <h2 className="ab-video__h2">DRIVING QUALITY<br /><span> ENSURING SAFETY</span></h2>
            <p className="ab-video__sub">
              We help you maintain your vehicle in a good condition and your driving style are the main factors influencing your fuel consumption.
            </p>
          </div>
        </div>
        <div className="ab-video__stripe" aria-hidden="true" />
        <div className="ab-vid-box__anchor">
          <div className="container ab-vid-box__wrapper">
            <div className={`ab-vid-box fade-in ${vidInView ? "visible" : ""}`} style={{ transitionDelay: "0.2s" }}>
              <div className="ab-vid-box__tabs">
                {vidTabs.map((t, i) => (
                  <button
                    key={i}
                    className={`ab-vid-box__tab ${activeTab === i ? "active" : ""}`}
                    onClick={() => setActiveTab(i)}
                    type="button"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="ab-vid-box__body">
                <div className="ab-vid-box__img-wrap">
                  <img src={tab.img} alt={tab.title} loading="lazy" draggable="false" onDragStart={e => e.preventDefault()} />
                </div>
                <div className="ab-vid-box__content">
                  <h3 className="ab-vid-box__title">{tab.title}</h3>
                  {loadingAbout ? (
                    <p>Loading…</p>
                  ) : (
                    <ul className="ab-vid-box__list">
                      {tab.bullets.map((b, bi) => (
                        <li key={bi}>
                          <span className="ab-vid-box__check" aria-hidden="true"><FiCheck size={11} /></span>
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════ 5. CORE VALUES ══════ */}
      <section className="ab-values ab-values--fullwidth" ref={valRef}>
        <div className="ab-values__inner">
          {loadingAbout ? (
            <p style={{ textAlign: "center", padding: "2rem" }}>Loading values…</p>
          ) : coreValues.length > 0 ? (
            coreValues.map((v, i) => (
              <div
                key={i}
                className={`ab-val fade-in ${valInView ? "visible" : ""}`}
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <h3 className="ab-val__title">{v.title}</h3>
                <div className="ab-val__icon-wrap">
                  <img src={VALUE_ICONS[i % VALUE_ICONS.length]} alt={v.title} className="ab-val__icon" />
                </div>
                <p className="ab-val__desc">{v.desc}</p>
              </div>
            ))
          ) : (
            <p style={{ textAlign: "center", color: "#888", padding: "2rem" }}>No core values found.</p>
          )}
        </div>
        <div className="ab-values__stripe" aria-hidden="true" />
      </section>

      {/* ══════ 6. FAQ ACCORDION ══════ */}
      <section className="ab-faq" ref={faqRef}>
        <div className="container ab-faq__inner">

          {/* ── Left: accordion ── */}
          <div className={`ab-faq__left fade-in ${faqInView ? "visible" : ""}`}>
            <div className="ab-faq__tag">FAQ</div>
            <h2 className="ab-faq__h2">
              FREQUENTLY ASKED<br /><span>QUESTIONS</span>
            </h2>
            <p className="ab-faq__sub">
              Everything you need to know about our services, process and the vehicles we work on.
            </p>

            <div className="ab-faq__list">
              {loadingFaq ? (
                <div className="ab-faq__loading">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="ab-faq__skeleton" />
                  ))}
                </div>
              ) : faqData.length === 0 ? (
                <p className="ab-faq__empty">No FAQ items found.</p>
              ) : (
                faqData.map((item, i) => (
                  <div
                    key={item.id}
                    className={`ab-faq__item ${openFaq === i ? "ab-faq__item--open" : ""} fade-in ${faqInView ? "visible" : ""}`}
                    style={{ transitionDelay: `${i * 0.07}s` }}
                  >
                    <button
                      className="ab-faq__question"
                      onClick={() => handleFaqToggle(i, item.image_filename)}
                      aria-expanded={openFaq === i}
                      type="button"
                    >
                      <span className="ab-faq__num">{String(i + 1).padStart(2, "0")}</span>
                      <span className="ab-faq__q-text">{item.title}</span>
                      <span className="ab-faq__icon" aria-hidden="true">
                        {openFaq === i ? <FiMinus size={15} /> : <FiPlus size={15} />}
                      </span>
                    </button>
                    <div className="ab-faq__answer-wrap">
                      <div className="ab-faq__answer">
                        <p>{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ── Right: sticky image panel ── */}
          <div className={`ab-faq__right fade-in ${faqInView ? "visible" : ""}`} style={{ transitionDelay: "0.18s" }}>
            <div className="ab-faq__img-frame">

              {/* Badge */}
              <div className="ab-faq__badge">
                <span className="ab-faq__badge-num">{faqData.length || 6}</span>
                <span className="ab-faq__badge-lbl">Questions<br />Answered</span>
              </div>

              {/* Main active image */}
              <div className="ab-faq__img-main-wrap">
                <img
                  key={activeFaqImg}
                  src={activeFaqImg}
                  alt="FAQ illustration"
                  className="ab-faq__img-main"
                  draggable="false"
                  onDragStart={e => e.preventDefault()}
                />
              </div>

              {/* Decorative corner border */}
              <div className="ab-faq__img-accent" aria-hidden="true" />

              {/* Thumbnail strip */}
              <div className="ab-faq__thumbs">
                {faqData.map((item, i) => {
                  const thumb = FAQ_IMAGE_MAP[item.image_filename];
                  if (!thumb) return null;
                  return (
                    <button
                      key={item.id}
                      className={`ab-faq__thumb ${openFaq === i ? "ab-faq__thumb--active" : ""}`}
                      onClick={() => handleFaqToggle(i, item.image_filename)}
                      type="button"
                      title={item.title}
                    >
                      <img
                        src={thumb}
                        alt={item.title}
                        draggable="false"
                        onDragStart={e => e.preventDefault()}
                      />
                    </button>
                  );
                })}
              </div>

            </div>
          </div>
        </div>
      </section>

    </main>
  );
}